import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Download, Printer, TrendingUp, TrendingDown,
  Building2, Calendar, AlertCircle, CheckCircle2, DollarSign
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/format';
import FinancialHeader from './FinancialHeader';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

export default function FinancialReports() {
  const [ownerCompanyId, setOwnerCompanyId] = useState('all');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [reportType, setReportType] = useState('dre'); // 'dre', 'inadimplencia', 'extrato'
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().substring(0, 10));
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/owner-companies');
        setOwnerCompanies(res.data?.data || res.data || []);
      } catch (err) {
        console.error('Erro ao buscar empresas', err);
      }
    })();
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: 1000,
        start_date: startDate,
        end_date: endDate,
      };
      if (ownerCompanyId && ownerCompanyId !== 'all') params.owner_company_id = ownerCompanyId;

      const res = await api.get('/financial/transactions', { params });
      const raw = res.data;
      const txs = raw?.items || raw?.data || (Array.isArray(raw) ? raw : []);

      // Calculate DRE items
      const grossRevenue = txs.filter(t => t.type === 'revenue').reduce((acc, t) => acc + (t.amount || 0), 0);
      const deductions = txs.filter(t => t.type === 'revenue' && t.discount_amount > 0).reduce((acc, t) => acc + t.discount_amount, 0);
      const netRevenue = grossRevenue - deductions;

      const operationalCosts = txs.filter(t => t.type === 'expense' && (t.classification === 'variable' || t.classification === 'operational' || t.origin === 'maintenance_order' || t.origin === 'fuel_record')).reduce((acc, t) => acc + (t.amount || 0), 0);
      const grossMargin = netRevenue - operationalCosts;

      const fixedCosts = txs.filter(t => t.type === 'expense' && t.classification === 'fixed').reduce((acc, t) => acc + (t.amount || 0), 0);
      const otherExpenses = txs.filter(t => t.type === 'expense' && !['variable', 'operational', 'fixed', 'extraordinary'].includes(t.classification) && !['maintenance_order', 'fuel_record'].includes(t.origin)).reduce((acc, t) => acc + (t.amount || 0), 0);

      const ebitda = grossMargin - fixedCosts - otherExpenses;
      const extraordinary = txs.filter(t => t.type === 'expense' && t.classification === 'extraordinary').reduce((acc, t) => acc + (t.amount || 0), 0);
      const netResult = ebitda - extraordinary;

      // Inadimplência
      const todayStr = new Date().toISOString().substring(0, 10);
      const overdueReceivables = txs.filter(t => t.type === 'revenue' && t.payment_status === 'pending' && t.due_date < todayStr);

      setReportData({
        transactions: txs,
        dre: {
          grossRevenue,
          deductions,
          netRevenue,
          operationalCosts,
          grossMargin,
          fixedCosts,
          otherExpenses,
          ebitda,
          extraordinary,
          netResult,
        },
        overdueReceivables,
      });
    } catch (err) {
      console.error('Erro ao gerar relatório', err);
      toast.error('Erro ao gerar dados do relatório');
    } finally {
      setLoading(false);
    }
  }, [ownerCompanyId, startDate, endDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    if (!reportData?.transactions?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    const headers = ['Vencimento', 'Pagamento', 'Tipo', 'Classificação', 'Descrição', 'Entidade', 'Empresa', 'Valor', 'Status'];
    const rows = reportData.transactions.map(t => [
      t.due_date || '',
      t.payment_date || '',
      t.type || '',
      t.classification || '',
      `"${(t.description || '').replace(/"/g, '""')}"`,
      `"${(t.client_name || t.supplier_name || t.vehicle_plate || '').replace(/"/g, '""')}"`,
      `"${(t.owner_company_name || '').replace(/"/g, '""')}"`,
      (t.amount || 0).toFixed(2),
      t.payment_status || ''
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `relatorio_${reportType}_${startDate}_a_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Relatório CSV exportado com sucesso');
  };

  const dre = reportData?.dre || {};

  return (
    <div className="space-y-6">
      <FinancialHeader
        title="Relatórios Financeiros"
        subtitle="DRE Gerencial, análise de inadimplência e exportações contábeis"
        ownerCompanyId={ownerCompanyId}
        setOwnerCompanyId={setOwnerCompanyId}
        ownerCompanies={ownerCompanies}
        onRefresh={loadReport}
      />

      {/* Toolbar */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4 print:hidden">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="h-9 w-52 text-xs font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dre">DRE Gerencial Simplificado</SelectItem>
              <SelectItem value="inadimplencia">Relatório de Inadimplência</SelectItem>
              <SelectItem value="extrato">Extrato Analítico Geral</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1.5 text-xs text-neutral-500">
            <Calendar className="h-4 w-4 text-neutral-400" />
            <span>Período:</span>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-8 w-36 text-xs"
            />
            <span>até</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-8 w-36 text-xs"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            className="h-9 text-xs gap-1.5"
          >
            <Printer className="h-3.5 w-3.5" />
            Imprimir
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="h-9 text-xs gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Relatório 1: DRE Gerencial */}
      {reportType === 'dre' && (
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-6 shadow-xs max-w-4xl mx-auto">
          <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4 mb-6">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              Demonstrativo de Resultado do Exercício (DRE Gerencial)
            </h2>
            <p className="text-xs text-neutral-500 mt-1">
              Período de apuração: {formatDate(startDate)} a {formatDate(endDate)} • Empresa: {ownerCompanyId === 'all' ? 'Todas as Empresas (Consolidado)' : ownerCompanies.find(c => c.id === ownerCompanyId)?.name || 'Dany Trans'}
            </p>
          </div>

          <div className="space-y-3 text-sm">
            {/* Receitas */}
            <div className="flex justify-between font-semibold py-2 border-b border-neutral-100 dark:border-neutral-800">
              <span className="text-neutral-900 dark:text-neutral-100">1. RECEITA OPERACIONAL BRUTA</span>
              <span className="text-emerald-600 font-bold">{formatCurrency(dre.grossRevenue || 0)}</span>
            </div>

            <div className="flex justify-between pl-6 text-neutral-500 text-xs py-1">
              <span>(-) Deduções, Descontos e Devoluções</span>
              <span className="text-red-500">({formatCurrency(dre.deductions || 0)})</span>
            </div>

            <div className="flex justify-between font-bold py-2 bg-neutral-50 dark:bg-neutral-800/40 px-3 rounded-lg">
              <span>(=) RECEITA OPERACIONAL LÍQUIDA</span>
              <span className="text-emerald-700 dark:text-emerald-400">{formatCurrency(dre.netRevenue || 0)}</span>
            </div>

            {/* Custos Operacionais */}
            <div className="flex justify-between pl-6 text-neutral-600 dark:text-neutral-400 py-1.5 border-b border-neutral-100 dark:border-neutral-800">
              <span>(-) Custos Operacionais Diretos (Combustível, Manutenção OS, Licenças de Frota)</span>
              <span className="text-red-600 font-medium">({formatCurrency(dre.operationalCosts || 0)})</span>
            </div>

            <div className="flex justify-between font-bold py-2 bg-neutral-50 dark:bg-neutral-800/40 px-3 rounded-lg">
              <span>(=) MARGEM DE CONTRIBUIÇÃO BRUTA</span>
              <span className={dre.grossMargin >= 0 ? 'text-neutral-900 dark:text-neutral-100' : 'text-red-600'}>
                {formatCurrency(dre.grossMargin || 0)}
              </span>
            </div>

            {/* Despesas Fixas */}
            <div className="flex justify-between pl-6 text-neutral-600 dark:text-neutral-400 py-1.5">
              <span>(-) Despesas Fixas & Administrativas</span>
              <span className="text-red-600 font-medium">({formatCurrency(dre.fixedCosts || 0)})</span>
            </div>

            {dre.otherExpenses > 0 && (
              <div className="flex justify-between pl-6 text-neutral-600 dark:text-neutral-400 py-1">
                <span>(-) Outras Despesas Operacionais</span>
                <span className="text-red-600 font-medium">({formatCurrency(dre.otherExpenses)})</span>
              </div>
            )}

            <div className="flex justify-between font-bold py-2.5 bg-blue-50/60 dark:bg-blue-950/30 px-3 rounded-lg text-blue-900 dark:text-blue-300">
              <span>(=) RESULTADO OPERACIONAL (EBITDA)</span>
              <span>{formatCurrency(dre.ebitda || 0)}</span>
            </div>

            {/* Extraordinários */}
            <div className="flex justify-between pl-6 text-neutral-600 dark:text-neutral-400 py-1.5 border-b border-neutral-100 dark:border-neutral-800">
              <span>(-) Custos Extraordinários / Imprevistos da Frota</span>
              <span className="text-red-600 font-medium">({formatCurrency(dre.extraordinary || 0)})</span>
            </div>

            {/* Resultado Líquido */}
            <div className="flex justify-between font-extrabold text-base py-3.5 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900 px-4 rounded-xl shadow-xs mt-4">
              <span>(=) RESULTADO LÍQUIDO DO PERÍODO</span>
              <span className={dre.netResult >= 0 ? 'text-emerald-400 dark:text-emerald-600' : 'text-red-400 dark:text-red-600'}>
                {formatCurrency(dre.netResult || 0)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Relatório 2: Inadimplência */}
      {reportType === 'inadimplencia' && (
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-xs">
          <div className="p-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
                Relatório de Inadimplência de Clientes
              </h3>
              <p className="text-xs text-neutral-500">
                Faturas vencidas não pagas até a presente data
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-neutral-400 uppercase font-semibold">Total em Atraso</p>
              <p className="text-lg font-bold text-red-600">
                {formatCurrency((reportData?.overdueReceivables || []).reduce((acc, t) => acc + (t.amount || 0), 0))}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-neutral-50 dark:bg-neutral-800/50 text-neutral-500 font-semibold border-b border-neutral-200 dark:border-neutral-800">
                <tr>
                  <th className="py-3 px-4">Vencimento</th>
                  <th className="py-3 px-4">Cliente</th>
                  <th className="py-3 px-4">Contrato / Descrição</th>
                  <th className="py-3 px-4">Empresa</th>
                  <th className="py-3 px-4 text-right">Valor em Aberto</th>
                  <th className="py-3 px-4 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {(reportData?.overdueReceivables || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-neutral-400">
                      <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
                      Nenhum cliente inadimplente neste período!
                    </td>
                  </tr>
                ) : (
                  reportData.overdueReceivables.map((t) => (
                    <tr key={t.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40">
                      <td className="py-3 px-4 font-semibold text-red-600">
                        {formatDate(t.due_date)}
                      </td>
                      <td className="py-3 px-4 font-bold text-neutral-900 dark:text-neutral-100">
                        🏢 {t.client_name || 'Cliente'}
                      </td>
                      <td className="py-3 px-4 text-neutral-700 dark:text-neutral-300">
                        {t.description}
                      </td>
                      <td className="py-3 px-4 text-neutral-600 dark:text-neutral-400">
                        {t.owner_company_name || 'Dany Trans'}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-red-600">
                        {formatCurrency(t.amount)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]">
                          Vencido
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Relatório 3: Extrato Analítico */}
      {reportType === 'extrato' && (
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-xs">
          <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
            <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
              Extrato Analítico para Contabilidade
            </h3>
            <p className="text-xs text-neutral-500">
              Listagem completa de todos os lançamentos ocorridos entre {formatDate(startDate)} e {formatDate(endDate)}
            </p>
          </div>

          <div className="overflow-x-auto max-h-[500px]">
            <table className="w-full text-xs text-left">
              <thead className="bg-neutral-50 dark:bg-neutral-800/50 text-neutral-500 font-semibold sticky top-0 border-b border-neutral-200 dark:border-neutral-800">
                <tr>
                  <th className="py-3 px-4">Vencimento</th>
                  <th className="py-3 px-4">Tipo</th>
                  <th className="py-3 px-4">Descrição</th>
                  <th className="py-3 px-4">Entidade</th>
                  <th className="py-3 px-4">Empresa</th>
                  <th className="py-3 px-4 text-right">Valor</th>
                  <th className="py-3 px-4 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {(reportData?.transactions || []).map((t) => (
                  <tr key={t.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40">
                    <td className="py-2.5 px-4 font-medium">{formatDate(t.due_date)}</td>
                    <td className="py-2.5 px-4 font-semibold capitalize">{t.type === 'revenue' ? 'Receita' : 'Despesa'}</td>
                    <td className="py-2.5 px-4 text-neutral-800 dark:text-neutral-200 truncate max-w-xs">{t.description}</td>
                    <td className="py-2.5 px-4 text-neutral-600 dark:text-neutral-400">{t.client_name || t.supplier_name || t.vehicle_plate || '-'}</td>
                    <td className="py-2.5 px-4 text-neutral-600 dark:text-neutral-400">{t.owner_company_name || 'Dany Trans'}</td>
                    <td className="py-2.5 px-4 text-right font-bold">{formatCurrency(t.amount)}</td>
                    <td className="py-2.5 px-4 text-center capitalize">{t.payment_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
